import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// hooks
import useAuth from 'src/hooks/useAuth';
import useIsMountedRef from 'src/hooks/useIsMountedRef';
// shadcn components
import { Button } from 'src/@/components/ui/button';
import { Input } from 'src/@/components/ui/input';
import { Checkbox } from 'src/@/components/ui/checkbox';
import { Label } from 'src/@/components/ui/label';
import { useToast } from 'src/@/components/ui/use-toast';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
// Custom components
import { ButtonLoading } from 'src/@/components/use/ButtonLoading';

// ----------------------------------------------------------------------

export default function LoginForm() {
    const navigate = useNavigate();
    const { login } = useAuth();
    const isMountedRef = useIsMountedRef();
    const { toast } = useToast();
    const { t } = useTranslation('auth');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [formErrors, setFormErrors] = useState({ email: false, password: false });
    const [email, setEmail] = useState('');
    const [senha, setSenha] = useState('');
    const [checkManterLogado, setCheckManterLogado] = useState(false);

    const handleShowPassword = () => {
        setShowPassword((show) => !show);
    };

    const handleKeyUp = (event) => {
        if (!event.shiftKey && event.key === 'Enter') {
            onLogin();
        }
    };

    const validateForm = () => {
        const errors = {
            email: !email.trim(),
            password: !senha.trim(),
        };
        setFormErrors(errors);
        return !Object.values(errors).some((error) => error);
    };

    useEffect(() => {
        const storedCredenciais = JSON.parse(localStorage.getItem('credenciais'));
        if (storedCredenciais) {
            setEmail(storedCredenciais.email || '');
            setSenha(storedCredenciais.password || '');
            setCheckManterLogado(storedCredenciais.checkManterLogado || false);
        }
    }, []);

    const showErrorToast = (message) => {
        toast({
            variant: 'destructive',
            title: t('login.title'),
            description: message,
        });
    };

    async function onLogin() {
        if (!validateForm()) {
            showErrorToast(t('login.errors.fillRequired'));
            return;
        }
        setLoading(true);
        try {
            // Trimming email and password before sending to API
            const trimmedEmail = email.trim();
            const trimmedSenha = senha.trim();
            const resLogin = await login(trimmedEmail, trimmedSenha, checkManterLogado);

            if (isMountedRef.current) {
                setLoading(false);
            }

            if (resLogin.error) {
                if (resLogin.action === 'error') {
                    showErrorToast(resLogin.message || t('login.errors.genericErrorCode', { code: '01' }));
                } else if (resLogin.action === 'user_pass_error') {
                    showErrorToast(t('login.errors.invalidCredentials'));
                }
            }
        } catch (e) {
            if (isMountedRef.current) {
                setLoading(false);
            }
            console.error('Login error:', e);
            showErrorToast(t('login.errors.genericErrorCode', { code: '02' }));
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault();
        onLogin();
    };

    return (
        <form className="w-full" onSubmit={handleSubmit} autoComplete="on">
            <div className="space-y-5">
                <div className="space-y-1.5">
                    <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                        {t('login.email')}
                    </Label>
                    <div className="relative">
                        <Input
                            id="email"
                            value={email}
                            onKeyPress={handleKeyUp}
                            autoComplete="email"
                            type="email"
                            placeholder="seu@email.com"
                            className={`rounded-xl border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20 ${formErrors.email ? 'border-red-400 focus:border-red-400 focus:ring-red-400/20' : ''}`}
                            onChange={(event) => setEmail(event.target.value)}
                        />
                        {formErrors.email && <p className="text-red-500 text-xs mt-1.5">{t('login.errors.emailRequired')}</p>}
                    </div>
                </div>

                <div className="space-y-1.5">
                    <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                        {t('login.password')}
                    </Label>
                    <div className="relative">
                        <Input
                            id="password"
                            value={senha}
                            onKeyPress={handleKeyUp}
                            autoComplete="current-password"
                            type={showPassword ? 'text' : 'password'}
                            placeholder=""
                            className={`rounded-xl border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20 pr-10 ${formErrors.password ? 'border-red-400 focus:border-red-400 focus:ring-red-400/20' : ''}`}
                            onChange={(event) => setSenha(event.target.value)}
                        />
                        <button type="button" onClick={handleShowPassword} className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-0.5 text-gray-400 hover:text-gray-600 transition-colors">
                            {showPassword ? <EyeOffIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                        </button>
                        {formErrors.password && <p className="text-red-500 text-xs mt-1.5">{t('login.errors.passwordRequired')}</p>}
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-between mt-5">
                <div className="flex items-center space-x-2">
                    <Checkbox
                        id="remember"
                        checked={checkManterLogado}
                        onCheckedChange={setCheckManterLogado}
                        className="data-[state=checked]:bg-[#7C3AED] data-[state=checked]:border-[#7C3AED]"
                    />
                    <Label htmlFor="remember" className="text-xs font-medium text-gray-500 cursor-pointer">
                        {t('login.rememberMe')}
                    </Label>
                </div>
            </div>

            <ButtonLoading
                type="submit"
                className="w-full mt-6 bg-[#7C3AED] hover:bg-[#6d28d9] text-white text-sm font-medium rounded-xl transition-all shadow-sm shadow-[#7C3AED]/20 active:transform active:scale-95"
                loading={loading}
            >
                {t('login.submit')}
            </ButtonLoading>
        </form>
    );
}